import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { T } from "../tokens";
import { Btn } from "../components/Btn";
import { AuthShell } from "../components/AuthShell";
import { PasswordField } from "../components/PasswordField";
import { GoogleOAuthButton, useGoogleEnabled } from "../components/GoogleOAuthButton";
import { useLogin, useSignup } from "../hooks/useAuth";
import { PasswordStrengthMeter } from "../components/PasswordStrengthMeter";
import { meetsMinimum, passwordsMatch, MIN_PASSWORD_LENGTH } from "../lib/password-strength";
import { useLaunchConfig } from "../lib/launch-config";
import { track } from "../lib/analytics";
import { usePageMeta } from "../lib/page-meta";
import { LegalLinks } from "../components/LegalLinks";

export function Login() {
  const navigate = useNavigate();
  const [mode, setMode]       = useState<"login" | "signup">("login");
  const [email, setEmail]     = useState("");
  const [name,  setName]      = useState("");
  const [password, setPass]   = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError]     = useState<string | null>(null);
  const [signedUp, setSignedUp] = useState(false);   // post-signup "check your email" state

  const login  = useLogin();
  const signup = useSignup();
  const googleEnabled = useGoogleEnabled();
  // v1.1 launch flags (all OFF → this page is unchanged from r2b).
  const { flags } = useLaunchConfig();
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [website, setWebsite] = useState("");                 // honeypot: humans never see or fill it
  // v1.1 bot protection: elapsed time on a monotonic clock — device clock skew cannot break it
  const [formOpenedAt] = useState(() => performance.now());
  const qc = useQueryClient();
  usePageMeta("Sign in or create your account — Calibre", "Upload your résumé and get tech jobs in India scored against your experience.");

  const isSignup = mode === "signup";
  // Client-side signup gates (server is the authority; these are UX guards).
  const tooShort = isSignup && password.length > 0 && !meetsMinimum(password);
  const mismatch = isSignup && confirm.length > 0 && !passwordsMatch(password, confirm);
  const signupReady = !isSignup || (meetsMinimum(password) && passwordsMatch(password, confirm) && name.length >= 2
    && (!flags.signupConsent || acceptTerms));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (isSignup && !signupReady) {
      setError(!meetsMinimum(password)
        ? `Password must be at least ${MIN_PASSWORD_LENGTH} characters`
        : "Passwords do not match");
      return;
    }
    try {
      if (mode === "login") {
        await login.mutateAsync({ email, password });
        navigate("/");
      } else {
        // Signup logs the user in (register returns tokens) but verification is
        // SOFT — show a "check your email" step; they can continue to the app.
        await signup.mutateAsync({
          email, password, name,
          ...(flags.signupConsent ? { acceptTerms } : {}),
          ...(flags.signupBotProtection ? { website, formFillMs: Math.round(performance.now() - formOpenedAt) } : {}),
        });
        track("signup", { method: "password" });
        setSignedUp(true);
      }
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: string; code?: string } } })?.response?.data;
      const msg = data?.error;
      // The server needs fields this page did not show (its launch config was stale or failed
      // to load): reload the config so the checkbox / timing appear, then the user retries.
      if (data?.code === "consent_required" || data?.code === "signup_rejected") void qc.invalidateQueries({ queryKey: ["public-config"] });
      setError(msg ?? "Something went wrong");
    }
  }

  const busy = login.isPending || signup.isPending;

  function switchMode() {
    setMode(m => m === "login" ? "signup" : "login");
    setConfirm("");
    setError(null);
  }

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "10px 12px",
    background: T.s2, border: `1px solid ${T.b1}`,
    borderRadius: "7px", color: T.t0,
    fontFamily: T.body, fontSize: "13px",
    outline: "none",
  };

  const cardStyle: React.CSSProperties = {
    width: "100%", padding: "32px", background: T.s1,
    border: `1px solid ${T.b0}`, borderRadius: "12px",
  };

  // Post-signup: soft email-verification step. The account is already usable.
  if (signedUp) {
    return (
      <AuthShell>
        <div style={{ ...cardStyle, textAlign: "center" }}>
          <div style={{ fontSize: "34px", marginBottom: "8px" }}>✉️</div>
          <h1 style={{ fontFamily: T.display, fontSize: "20px", fontWeight: 700, color: T.t0, margin: "0 0 8px" }}>
            Check your email
          </h1>
          <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, lineHeight: 1.6, margin: "0 0 20px" }}>
            We've sent a verification link to <strong style={{ color: T.t1 }}>{email}</strong>.
            Verifying secures your account — but you can start using Caliber right now.
          </p>
          <p style={{ fontFamily: T.body, fontSize: "11px", color: T.t3, lineHeight: 1.6, margin: "0 0 20px" }}>
            Caliber is currently in Beta — recommendations may be incomplete or inaccurate,
            and some role categories may have limited inventory. Your resume information is
            processed to generate job matches; Caliber does not automatically apply for jobs
            on your behalf. {flags.honestCopy
              ? (flags.dataExport ? "You can download your data or delete your account at any time from Profile." : "You can delete your account at any time from Profile.")
              : "Contact support to request account or resume deletion."}
          </p>
          <Btn variant="cta" size="md" onClick={() => navigate("/")}>Continue to Caliber</Btn>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div style={cardStyle}>
        <div style={{ marginBottom: "24px" }}>
          <h1 style={{ fontFamily: T.display, fontSize: "24px", fontWeight: 700, color: T.t0 }}>
            Caliber <span style={{ color: T.teal }}>by DevAscend</span>
          </h1>
          <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, marginTop: "4px" }}>
            {mode === "login" ? "Sign in to your account" : "Create a new account"}
          </p>
          {isSignup && (
            <p style={{ fontFamily: T.body, fontSize: "11px", color: T.t3, marginTop: "8px", lineHeight: 1.5 }}>
              Caliber is in Beta. Recommendations may be incomplete or inaccurate. Resume
              information is processed to generate job matches — Caliber never auto-applies
              on your behalf.
            </p>
          )}
        </div>

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {isSignup && (
            <input style={inputStyle} type="text" placeholder="Full name" autoComplete="name"
              value={name} onChange={e => setName(e.target.value)} required />
          )}
          <input style={inputStyle} type="email" placeholder="Email" autoComplete="email"
            value={email} onChange={e => setEmail(e.target.value)} required />

          <PasswordField
            value={password} onChange={setPass}
            placeholder={isSignup ? `Password (min ${MIN_PASSWORD_LENGTH} chars)` : "Password"}
            autoComplete={isSignup ? "new-password" : "current-password"}
            required style={inputStyle}
          />
          {isSignup && <PasswordStrengthMeter password={password} />}

          {!isSignup && (
            <div style={{ textAlign: "right", marginTop: "2px" }}>
              <button type="button" onClick={() => navigate("/forgot-password")}
                style={{ background: "none", border: "none", color: T.tealSoft, cursor: "pointer",
                  fontFamily: T.body, fontSize: "12.5px", fontWeight: 600, padding: 0,
                  textDecoration: "underline", textUnderlineOffset: "2px" }}>
                Forgot password?
              </button>
            </div>
          )}

          {isSignup && (
            <>
              <PasswordField
                value={confirm} onChange={setConfirm}
                placeholder="Confirm password" autoComplete="new-password"
                required style={inputStyle}
              />
              {mismatch && (
                <span style={{ fontFamily: T.body, fontSize: "11px", color: T.err }}>
                  Passwords do not match
                </span>
              )}
              {tooShort && !mismatch && (
                <span style={{ fontFamily: T.body, fontSize: "11px", color: T.err }}>
                  Password must be at least {MIN_PASSWORD_LENGTH} characters
                </span>
              )}
            </>
          )}

          {isSignup && flags.signupBotProtection && (
            <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "hidden" }}>
              <label>Website<input type="text" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={e => setWebsite(e.target.value)} /></label>
            </div>
          )}
          {isSignup && flags.signupConsent && (
            <label data-testid="consent" style={{ display: "flex", gap: "8px", alignItems: "flex-start", fontFamily: T.body, fontSize: "12px", color: T.t2, lineHeight: 1.5 }}>
              <input type="checkbox" checked={acceptTerms} onChange={e => setAcceptTerms(e.target.checked)} required style={{ marginTop: "2px" }} />
              <span>I am 18 or older and agree to the{" "}
                <a href="/terms" target="_blank" rel="noopener" style={{ color: T.tealSoft }}>Terms</a> and{" "}
                <a href="/privacy" target="_blank" rel="noopener" style={{ color: T.tealSoft }}>Privacy Policy</a>, including processing my résumé to find job matches.</span>
            </label>
          )}

          {error && (
            <p style={{ fontFamily: T.body, fontSize: "12px", color: T.err }}>{error}</p>
          )}

          <Btn type="submit" variant="cta" size="md" disabled={busy || !signupReady}>
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </Btn>
        </form>

        {googleEnabled && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", margin: "16px 0" }}>
              <span style={{ flex: 1, height: "1px", background: T.b1 }} />
              <span style={{ fontFamily: T.body, fontSize: "11px", color: T.t2 }}>or</span>
              <span style={{ flex: 1, height: "1px", background: T.b1 }} />
            </div>
            <GoogleOAuthButton />
            {flags.signupConsent && (
              <p data-testid="google-consent-notice" style={{ fontFamily: T.body, fontSize: "11px", color: T.t3, marginTop: "8px", textAlign: "center", lineHeight: 1.5 }}>
                By continuing with Google you confirm you are 18 or older and agree to the{" "}
                <a href="/terms" target="_blank" rel="noopener" style={{ color: T.tealSoft }}>Terms</a> and{" "}
                <a href="/privacy" target="_blank" rel="noopener" style={{ color: T.tealSoft }}>Privacy Policy</a>.
              </p>
            )}
          </>
        )}

        <p style={{ fontFamily: T.body, fontSize: "12px", color: T.t2, textAlign: "center", marginTop: "16px" }}>
          {mode === "login" ? "No account? " : "Already have one? "}
          <button
            type="button"
            onClick={switchMode}
            style={{ background: "none", border: "none", color: T.tealSoft, cursor: "pointer", fontFamily: T.body, fontSize: "12px" }}
          >
            {mode === "login" ? "Sign up" : "Sign in"}
          </button>
        </p>
        <LegalLinks />
      </div>
    </AuthShell>
  );
}
